import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGetV2ProviderDocumentsPromptsQuery } from '@features/markdown-editor-ai/store/api';
import type { CustomPromptResponse } from '@features/markdown-editor-ai/types';
import {
  useFeatureFlag,
  useGetMarketingCustomizationsQuery,
  useOnboardingStep,
  useUpdateOnboardingStep,
} from '@features/user';
import { Description as DocumentIcon, Lightbulb as LightbulbIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';

import { NewNotification, ToolbarButton } from '../components';
import { useNotificationTracking } from '../hooks';
import { useToolbarAction } from '../store/hooks';
import { type ToolbarActionProps } from '../types';

import { CustomTemplateSelector } from './CustomTemplateSelector';

interface DocumentTemplate {
  name: string;
  prompt: string;
}

const DOCUMENT_TEMPLATES: DocumentTemplate[] = [
  {
    name: 'ESA Letter',
    prompt: 'Generate a letter verifying the need for an Emotional Support Animal.',
  },
  {
    name: 'Academic Accommodation',
    prompt: 'Generate a letter requesting academic accommodations for a student.',
  },
  {
    name: 'Workplace Accommodation',
    prompt: 'Generate a letter requesting workplace accommodations for an employee.',
  },
  {
    name: 'Housing Accommodation',
    prompt: 'Generate a letter requesting housing accommodations or modifications.',
  },
  {
    name: 'Treatment Plan',
    prompt: 'Generate a treatment plan outlining goals and interventions.',
  },
  {
    name: 'Discharge Summary',
    prompt: 'Generate a summary of treatment and outcomes at discharge.',
  },
  {
    name: 'Forensic Report',
    prompt: 'Generate a report providing a professional opinion for legal or forensic purposes.',
  },
  {
    name: 'Substance Use Evaluation',
    prompt: 'Generate a report summarizing a substance use evaluation.',
  },
];

export const GenerateDocumentAction: React.FC<ToolbarActionProps> = ({ context, compact }) => {
  const { open } = useModal();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isEnabled = useFeatureFlag('document_generation');
  const { badgeCount } = useToolbarAction('generate-document');
  const { toolbar, document } = useEventTracking();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [documentTypeInput, setDocumentTypeInput] = useState('');
  const anchorRef = useRef<HTMLDivElement>(null);

  // Onboarding state management
  const isDocGenOnboarded = useOnboardingStep('document_generation_notification_acknowledged');
  const updateOnboardingStep = useUpdateOnboardingStep();
  const notificationTracking = useNotificationTracking('document_generation');
  const [hasRecordedShowing, setHasRecordedShowing] = useState(false);
  const [notificationStartTime, setNotificationStartTime] = useState<number | null>(null);

  const { data: customTemplatesData } = useGetV2ProviderDocumentsPromptsQuery();
  const hasCustomTemplates = (customTemplatesData?.prompts?.length ?? 0) > 0;
  const { data: marketingCustomizations } = useGetMarketingCustomizationsQuery();

  // Use personalized suggestions if available, otherwise randomly select
  const suggestedDocuments = useMemo(() => {
    const customizationsData = marketingCustomizations?.customizations as
      | { document_generator_likely_documents?: string[] }
      | undefined;
    const personalizedDocs = customizationsData?.document_generator_likely_documents;

    if (personalizedDocs && personalizedDocs.length > 0) {
      // Use personalized documents (take up to 4)
      return personalizedDocs
        .slice(0, 4)
        .map((name: string) => ({ name, prompt: `Generate a ${name}.` }));
    }

    // Fallback to random selection
    const shuffled = [...DOCUMENT_TEMPLATES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  }, [marketingCustomizations]);

  // Determine if notification should be shown
  const shouldShowNotification = useMemo(() => {
    // Don't show if feature is disabled
    if (!isEnabled) return false;

    // Don't show if no patient context
    if (!context?.patientId) return false;

    // Don't show in encounter view (active recording/documentation)
    if (context?.source === 'encounter') return false;

    // Don't show if already onboarded (this prevents showing after acknowledgment)
    if (isDocGenOnboarded) return false;

    // Check timing and show count
    return notificationTracking.shouldShowBasedOnTiming();
  }, [isEnabled, context?.patientId, context?.source, isDocGenOnboarded, notificationTracking]);

  // Record showing when notification is displayed
  useEffect(() => {
    if (shouldShowNotification && !hasRecordedShowing) {
      const daysSinceLast = notificationTracking.tracking.lastShown
        ? Math.floor((Date.now() - notificationTracking.tracking.lastShown) / (24 * 60 * 60 * 1000))
        : null;

      const personalizedDocs = (
        marketingCustomizations?.customizations as
          | { document_generator_likely_documents?: string[] }
          | undefined
      )?.document_generator_likely_documents;

      // Track notification shown event
      toolbar.notificationShown({
        notification_type: 'document_generation',
        show_count: notificationTracking.tracking.showCount + 1,
        is_personalized: !!personalizedDocs,
        personalized_count: personalizedDocs?.length,
        fallback_used: !personalizedDocs,
        messages: suggestedDocuments.slice(0, 3).map((d: DocumentTemplate) => d.name),
        days_since_last: daysSinceLast ?? undefined,
        trigger: notificationTracking.tracking.showCount === 0 ? 'initial' : 'retry',
      });

      setNotificationStartTime(Date.now());
      setHasRecordedShowing(true);

      // Auto-complete onboarding after 3 showings
      if (notificationTracking.tracking.showCount >= 2) {
        // Will be 3 after recordShowing
        // Track auto-completion
        toolbar.notificationAutoCompleted({
          notification_type: 'document_generation',
          total_showings: 3,
          never_acknowledged: true,
          is_personalized: !!personalizedDocs,
        });

        // Mark as complete after a delay to let the notification show
        setTimeout(() => {
          void updateOnboardingStep('document_generation_notification_acknowledged', true);
        }, 5000);
      }
    }
  }, [
    shouldShowNotification,
    hasRecordedShowing,
    notificationTracking,
    updateOnboardingStep,
    toolbar,
    marketingCustomizations,
    suggestedDocuments,
  ]);

  // Handle notification shown (popover opened)
  const handleNotificationShown = () => {
    notificationTracking.recordShowing();
  };

  // Handle notification acknowledgment
  const handleNotificationAcknowledge = async () => {
    const timeShown = notificationStartTime
      ? Math.round((Date.now() - notificationStartTime) / 1000)
      : 0;

    const personalizedDocs = (
      marketingCustomizations?.customizations as
        | { document_generator_likely_documents?: string[] }
        | undefined
    )?.document_generator_likely_documents;

    // Track acknowledgment event
    toolbar.notificationAcknowledged({
      notification_type: 'document_generation',
      acknowledgment_method: 'hover',
      time_shown_seconds: timeShown,
      show_count: notificationTracking.tracking.showCount,
      is_personalized: !!personalizedDocs,
      onboarding_completed: true,
    });

    // Mark onboarding as complete
    await updateOnboardingStep('document_generation_notification_acknowledged', true);

    // Mark notification as acknowledged
    notificationTracking.setAcknowledged();
  };

  // Check feature flag first
  if (!isEnabled) return null;

  // Show when we have a patient context (noteId is optional)
  if (!context?.patientId) {
    return null;
  }

  const handleButtonClick = () => {
    toolbar.actionClicked({
      action_type: 'generate_document',
    });

    // Open the popover
    setPopoverOpen(true);

    // Acknowledge notification if it's showing when clicked
    if (shouldShowNotification && !isDocGenOnboarded) {
      handleNotificationAcknowledge();
    }
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
    setDocumentTypeInput('');
  };

  const handleGenerateDocument = (prompt?: string, customPrompt?: CustomPromptResponse) => {
    const finalPrompt = prompt || documentTypeInput.trim();
    if ((finalPrompt || customPrompt) && context.patientId) {
      // Track document generation
      document.created({
        document_type: 'generated',
        source: 'toolbar',
        generation_method: 'ai_generated',
      });

      // Open the modal with the initial prompt or custom prompt
      // Note: The backend will need to fetch the full prompt based on the prompt_id
      open('GENERATE_DOCUMENT', {
        title: 'Documents',
        patientId: context.patientId,
        encounterId: context.encounterId,
        noteId: context.noteId,
        source: context.source,
        initialPrompt: finalPrompt,
        customPromptId: customPrompt?.prompt_id,
        customPromptName: customPrompt?.name,
        maxWidth: 'xl',
        fullWidth: !isMobile,
        fullHeight: !isMobile,
        fullScreen: isMobile,
      });

      handleClosePopover();
    }
  };

  const handleTemplateSelect = (template: CustomPromptResponse) => {
    handleGenerateDocument(undefined, template);
  };

  const handleChipClick = (template: DocumentTemplate) => {
    handleGenerateDocument(template.prompt);
  };

  return (
    <>
      <Box ref={anchorRef} sx={{ display: 'inline-flex' }}>
        <NewNotification
          enabled={shouldShowNotification}
          compact={compact}
          messages={[
            ...suggestedDocuments.slice(0, 3).map((doc: DocumentTemplate) => doc.name),
            'ANY Clinical Document',
          ]}
          onShow={handleNotificationShown}
        >
          <ToolbarButton
            icon={DocumentIcon}
            label="Documents"
            tooltip="Generate clinical documents based on this note"
            onClick={handleButtonClick}
            compact={compact}
            badgeCount={badgeCount}
            dataAttribute="toolbar-generate-document"
            disableTooltip={shouldShowNotification}
          />
        </NewNotification>
      </Box>
      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              mt: -1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                width: 20,
                height: 20,
                bgcolor: 'background.paper',
                transform: 'translateX(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            width: 420,
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            What kind of document do you need?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe the document that you need and JotPsych will create a draft for you.
          </Typography>

          {/* Show custom template selector if templates exist */}
          {hasCustomTemplates && (
            <CustomTemplateSelector onSelectTemplate={handleTemplateSelect} disabled={false} />
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            autoFocus
            placeholder="e.g., Prior authorization for MRI, Referral letter to cardiologist..."
            value={documentTypeInput}
            onChange={e => {
              setDocumentTypeInput(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleGenerateDocument();
              }
            }}
            sx={{ mb: 2 }}
          />
          {!documentTypeInput && !hasCustomTemplates && (
            <Box
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 1,
                mb: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LightbulbIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {suggestedDocuments.map((template: DocumentTemplate) => (
                  <Chip
                    key={template.name}
                    label={template.name}
                    onClick={() => handleChipClick(template)}
                    color="primary"
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      '& .MuiChip-label': {
                        px: 1,
                      },
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={handleClosePopover}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleGenerateDocument()}
              disabled={!documentTypeInput.trim()}
              sx={{ minWidth: 80 }}
            >
              Generate
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
